import { AxiosInstance, AxiosResponse } from 'axios';
import { get, patch, post } from '../hooks/useAxiosPrivate';
import { CompanyEntity, SalesModelEnum } from '../state/companies/types';
import { FeatureAccess } from '../config/companyFeatures';
import { JourneyTypeEnum } from '../state/journeys/types';
import { DataPayload } from '../state/types';
import { Auth } from '../state/auth/types';
import { isArtyc } from '../state/auth';

const ARTYC_COMPANY_URL = '/artyc/companies';
const companyUrl = (auth: Auth | null) => `/api/v1/company/${auth?.companyId}`;

const CompaniesApi = {
  getCompanies: async (
    axios: AxiosInstance,
    auth: Auth | null
  ): Promise<DataPayload<CompanyEntity>> => {
    // Choose the endpoint by role, not active org, so the Companies list stays
    // complete for Artyc even when an org is selected in the switcher.
    const url = isArtyc(auth) ? ARTYC_COMPANY_URL : companyUrl(auth);
    const response: AxiosResponse<DataPayload<CompanyEntity>> = await get(
      axios,
      url,
      { paginate: 'false' }
    );

    return response.data;
  },
  createCompany: async (
    axios: AxiosInstance,
    companyName: string,
    salesModel: SalesModelEnum,
    defaultJourneyType: JourneyTypeEnum,
    timezone?: string,
    parentCompanyId?: string,
    featureAccess?: FeatureAccess
  ) => {
    const response: AxiosResponse<CompanyEntity> = await post(
      axios,
      ARTYC_COMPANY_URL,
      {
        companyName,
        salesModel,
        defaultJourneyType,
        ...(timezone !== undefined ? { timezone } : {}),
        ...(parentCompanyId ? { parentCompany: parentCompanyId } : {}),
        ...(featureAccess ? { featureAccess } : {}),
      }
    );
    return response.data;
  },
  updateCompany: async (
    axios: AxiosInstance,
    companyId: string,
    companyName: string,
    salesModel: SalesModelEnum,
    defaultJourneyType: JourneyTypeEnum,
    parentCompany?: string | null,
    featureAccess?: FeatureAccess,
    timezone?: string
  ) => {
    const response: AxiosResponse<{ success: string }> = await patch(
      axios,
      `${ARTYC_COMPANY_URL}/${companyId}`,
      {
        companyName,
        salesModel,
        defaultJourneyType,
        // string => set parent, null => clear; omitted => unchanged
        ...(parentCompany !== undefined ? { parentCompany } : {}),
        ...(featureAccess !== undefined ? { featureAccess } : {}),
        ...(timezone !== undefined ? { timezone } : {}),
      }
    );
    return response.data;
  },
  archiveCompany: async (
    axios: AxiosInstance,
    companyId: string,
    reason?: string
  ) => {
    await post(axios, `${ARTYC_COMPANY_URL}/${companyId}/archive`, {
      ...(reason ? { reason } : {}),
    });
  },
};

export default CompaniesApi;
